import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Space,
  message,
} from "antd";
import {
  MailOutlined,
  LockOutlined,
  ArrowRightOutlined,
  GlobalOutlined,
  MoonOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AppHeaderLogo } from "../components/AppHeaderLogo";
import { GlobalUserMenu } from "../components/GlobalUserMenu";
import { useUiState } from "../state";
import {
  loginWithPassword,
  registerWithCode,
  sendRegisterCode,
} from "../services/compliance";

interface LoginPageProps {
  onLogin: (token: string) => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const { lang, themeMode, toggleLang, toggleTheme } = useUiState();
  const navigate = useNavigate();
  const [form] = Form.useForm<{
    email: string;
    password: string;
    confirmPassword: string;
    code: string;
    agree: boolean;
  }>();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [cooldown, setCooldown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [appMessage, contextHolder] = message.useMessage();

  const isDark = themeMode === "dark";

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(
      () => setCooldown((prev) => Math.max(0, prev - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const onSendCode = async () => {
    const email = form.getFieldValue("email");
    if (!email) {
      appMessage.warning(
        lang === "zh" ? "请先输入邮箱" : "Please enter your email first",
      );
      return;
    }
    if (cooldown > 0 || sendingCode) return;
    setSendingCode(true);
    try {
      const response = await sendRegisterCode(email);
      if (!response.ok) {
        const seconds = response.cooldown_seconds ?? 60;
        setCooldown(seconds);
        appMessage.warning(
          lang === "zh"
            ? `发送过于频繁，请 ${seconds}s 后重试`
            : `Too many requests. Retry in ${seconds}s`,
        );
        return;
      }
      setCooldown(60);
      appMessage.success(
        response.debug_code
          ? `${lang === "zh" ? "验证码已发送" : "Code sent"} (${response.debug_code})`
          : lang === "zh"
            ? "验证码已发送"
            : "Code sent",
      );
    } catch {
      appMessage.error(
        lang === "zh" ? "验证码发送失败" : "Failed to send verification code",
      );
    } finally {
      setSendingCode(false);
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (mode === "signup" && values.password !== values.confirmPassword) {
        appMessage.error(
          lang === "zh" ? "两次输入密码不一致" : "Passwords do not match",
        );
        return;
      }
      setSubmitting(true);
      try {
        if (mode === "signin") {
          const auth = await loginWithPassword({
            email: values.email,
            password: values.password,
          });
          onLogin(auth.access_token);
          return;
        }
        const auth = await registerWithCode({
          email: values.email,
          code: values.code,
          password: values.password,
        });
        onLogin(auth.access_token);
      } finally {
        setSubmitting(false);
      }
    } catch (error) {
      const fallback =
        lang === "zh" ? "登录或注册失败，请检查信息" : "Login/Register failed";
      const messageText =
        error instanceof Error && error.message ? error.message : fallback;
      appMessage.error(messageText);
    }
  };

  return (
    <div
      className={`login-page-wrap ${isDark ? "login-page-dark" : "login-page-light"}`}
    >
      {contextHolder}
      <header className="login-page-header">
        <div
          className="login-page-brand"
          onClick={() => navigate("/")}
          role="button"
        >
          <AppHeaderLogo />
          <span className="login-page-brand-text">XTool Compliance</span>
        </div>
        <div className="login-page-header-right">
          <Button
            type="text"
            className="icon-btn-pill"
            icon={<GlobalOutlined />}
            onClick={() => toggleLang()}
          >
            EN / 中文
          </Button>
          <Button
            type="text"
            className="icon-btn-pill icon-btn-pill-circle"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={() => toggleTheme(!isDark)}
          />
          <GlobalUserMenu />
        </div>
      </header>

      <main className="login-page-main">
        <div className="login-page-bg login-page-bg-1" />
        <div className="login-page-bg login-page-bg-2" />

        <div className="login-page-card">
          <div className="login-page-head">
            <h1 className="login-page-title">
              {lang === "zh" ? "欢迎回来" : "Welcome Back"}
            </h1>
            <p className="login-page-subtitle">
              Architectural Regulatory Intelligence Sentinel
            </p>
          </div>

          <div className="login-page-tabs">
            <button
              type="button"
              className={`login-page-tab ${mode === "signin" ? "active" : ""}`}
              onClick={() => setMode("signin")}
            >
              {lang === "zh" ? "登录" : "Sign In"}
            </button>
            <button
              type="button"
              className={`login-page-tab ${mode === "signup" ? "active" : ""}`}
              onClick={() => setMode("signup")}
            >
              {lang === "zh" ? "注册" : "Sign Up"}
            </button>
          </div>

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onSubmit}
            className="login-page-form"
          >
            <Form.Item
              label={lang === "zh" ? "邮箱" : "Email"}
              name="email"
              rules={[
                {
                  required: true,
                  message:
                    lang === "zh" ? "请输入邮箱" : "Email is required",
                },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="name@company.com"
                size="middle"
              />
            </Form.Item>

            {mode === "signin" ? (
              <>
                <Form.Item
                  label={
                    <span className="login-page-pwd-label">
                      {lang === "zh" ? "密码" : "Password"}
                      <a
                        href="#"
                        className="login-page-forgot"
                        onClick={(e) => e.preventDefault()}
                      >
                        {lang === "zh" ? "忘记?" : "Forgot?"}
                      </a>
                    </span>
                  }
                  name="password"
                  rules={[
                    {
                      required: true,
                      message:
                        lang === "zh" ? "请输入密码" : "Password is required",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="••••••••"
                    size="middle"
                  />
                </Form.Item>
                <Form.Item
                  name="agree"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (value) return;
                        throw new Error(
                          lang === "zh"
                            ? "请先阅读并同意使用协议"
                            : "Please read and accept the agreement first",
                        );
                      },
                    },
                  ]}
                >
                  <Checkbox className="login-page-checkbox">
                    {lang === "zh" ? "我同意" : "I agree to"}{" "}
                    <button
                      type="button"
                      className="login-page-link"
                      onClick={() => setAgreementOpen(true)}
                    >
                      {lang === "zh" ? "服务条款" : "Terms of Service"}
                    </button>{" "}
                    {lang === "zh" ? "和" : "and"}{" "}
                    <button
                      type="button"
                      className="login-page-link"
                      onClick={() => setAgreementOpen(true)}
                    >
                      {lang === "zh" ? "隐私政策" : "Privacy Policy"}
                    </button>
                    。
                  </Checkbox>
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="middle"
                  className="login-page-submit"
                  loading={submitting}
                >
                  {lang === "zh" ? "登录" : "Sign In"}
                  <ArrowRightOutlined className="login-page-submit-icon" />
                </Button>
              </>
            ) : (
              <>
                <Form.Item
                  label={lang === "zh" ? "验证码" : "Verification Code"}
                  required
                  className="login-page-code-field"
                >
                  <Space.Compact block className="login-page-code-compact">
                    <Form.Item
                      name="code"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message:
                            lang === "zh"
                              ? "请输入验证码"
                              : "Code is required",
                        },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined />}
                        placeholder={
                          lang === "zh"
                            ? "输入邮箱验证码"
                            : "Enter verification code"
                        }
                        size="middle"
                      />
                    </Form.Item>
                    <Button
                      type="default"
                      onClick={onSendCode}
                      disabled={cooldown > 0 || sendingCode}
                      loading={sendingCode}
                      size="middle"
                      className="login-page-send-btn"
                    >
                      {cooldown > 0
                        ? `${cooldown}s`
                        : lang === "zh"
                          ? "发送"
                          : "Send"}
                    </Button>
                  </Space.Compact>
                </Form.Item>
                <Form.Item
                  label={lang === "zh" ? "密码" : "Password"}
                  name="password"
                  rules={[
                    {
                      required: true,
                      message:
                        lang === "zh" ? "请输入密码" : "Password is required",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="••••••••"
                    size="middle"
                  />
                </Form.Item>
                <Form.Item
                  label={lang === "zh" ? "确认密码" : "Confirm Password"}
                  name="confirmPassword"
                  rules={[
                    {
                      required: true,
                      message:
                        lang === "zh"
                          ? "请再次输入密码"
                          : "Please confirm password",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="••••••••"
                    size="middle"
                  />
                </Form.Item>
                <Form.Item
                  name="agree"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (value) return;
                        throw new Error(
                          lang === "zh"
                            ? "请先阅读并同意使用协议"
                            : "Please read and accept the agreement first",
                        );
                      },
                    },
                  ]}
                >
                  <Checkbox className="login-page-checkbox">
                    {lang === "zh" ? "我同意" : "I agree to"}{" "}
                    <button
                      type="button"
                      className="login-page-link"
                      onClick={() => setAgreementOpen(true)}
                    >
                      {lang === "zh" ? "服务条款" : "Terms of Service"}
                    </button>{" "}
                    {lang === "zh" ? "和" : "and"}{" "}
                    <button
                      type="button"
                      className="login-page-link"
                      onClick={() => setAgreementOpen(true)}
                    >
                      {lang === "zh" ? "隐私政策" : "Privacy Policy"}
                    </button>
                    。
                  </Checkbox>
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="middle"
                  className="login-page-submit"
                  loading={submitting}
                >
                  {lang === "zh" ? "注册" : "Sign Up"}
                  <ArrowRightOutlined className="login-page-submit-icon" />
                </Button>
              </>
            )}
          </Form>
        </div>
      </main>

      <Modal
        open={agreementOpen}
        onCancel={() => setAgreementOpen(false)}
        onOk={() => setAgreementOpen(false)}
        okText={lang === "zh" ? "我已了解" : "I Understand"}
        cancelButtonProps={{ style: { display: "none" } }}
        title={lang === "zh" ? "服务条款" : "Service Terms"}
        className="login-page-modal"
      >
        <div className="login-page-modal-body">
          <section>
            <h4>{lang === "zh" ? "1. 工具用途" : "1. Tool Purpose"}</h4>
            <p>
              {lang === "zh"
                ? "XTool Compliance 旨在用于 FDA 合规性风险的识别和分析。我们的系统利用先进的智能技术为您的产品生命周期提供合规监护。"
                : "XTool Compliance is designed for identification and analysis of FDA regulatory risks. Our system utilizes advanced intelligence to sentinel your product compliance lifecycle."}
            </p>
          </section>
          <section>
            <h4>{lang === "zh" ? "2. 法律声明" : "2. Legal Disclaimer"}</h4>
            <p>
              {lang === "zh"
                ? "XTool Compliance 提供的信息仅供参考，不构成法律意见。监管环境是动态变化的；用户应咨询法律顾问或监管专业人员。"
                : "The information provided by XTool Compliance is for informational purposes only and does NOT constitute legal advice. Users should consult with legal counsel or regulatory professionals."}
            </p>
          </section>
          <section>
            <h4>{lang === "zh" ? "3. 使用建议" : "3. Usage Advice"}</h4>
            <p>
              {lang === "zh"
                ? "我们建议采用「人机协同」的方法。将我们的监护发现作为您专业合规审查流程的基础。"
                : "We recommend a human-in-the-loop approach. Use our sentinel findings as a foundation for your professional compliance review processes."}
            </p>
          </section>
        </div>
      </Modal>
    </div>
  );
}
